/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import { type ConditionsWithData, checkConditions } from '@mail-core/std';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import {
    freemiumConnectedBanner,
    freemiumDebugLogger,
    isHardFreemium,
    isSoftFreemium,
} from 'reactApp/appHelpers/featuresHelpers/features/freemium';
import { overquotaBannerConfig } from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import {
    preOverquotaBannerConfig,
    preOverquotaDebugLogger,
    preQuotaEnablePaid,
    preQuotaSizeLimit,
} from 'reactApp/appHelpers/featuresHelpers/features/overquotaPre';
import { overquotaDebugLogger } from 'reactApp/appHelpers/featuresHelpers/features/overquotaStates';
import { personalizedTopBannerEnabled } from 'reactApp/appHelpers/featuresHelpers/features/personalization';
import { forceVigboLinkedAccount, vigboTariffBannerRemindDays, vigboTariffs } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { useAiPhotoGenerationEntryPointBannerShow } from 'reactApp/hooks/useAiPhotoGenerationEntryPointBanner';
import { useDuplicatesEntryPointBanner } from 'reactApp/hooks/useDuplicatesEntryPointBanner';
import { useIsFreemium } from 'reactApp/hooks/useFreemium';
import { usePersonalizationTopBanner } from 'reactApp/hooks/usePersonalizationTopBanner';
import { useSubscriptionCommunicationBanners } from 'reactApp/hooks/useSubscriptionCommunicationBanners';
import { getLatestExpiredSubscription } from 'reactApp/modules/billingData/billingData.helpers';
import { BillingDataSelectors, getBillingDataLoadingState } from 'reactApp/modules/billingData/billingData.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { getVigboAlbums, getVigboLinkState, hasActiveVigboTariff } from 'reactApp/modules/vigbo/vigbo.selectors';
import { VigboAccountLinkState } from 'reactApp/modules/vigbo/vigbo.types';
import type { RootState } from 'reactApp/store';
import { AiPhotoGenerationPromoBanner } from 'reactApp/ui/AiPhotoGeneration/Promo/Banner/AiPhotoGenerationPromoBanner';
import { AutoRenewalBanner } from 'reactApp/ui/AutoRenewalBanner/AutoRenewalBanner';
import { DuplicatesPromoBanner } from 'reactApp/ui/Duplicates/DuplicatesPromoBanner/DuplicatesPromoBanner';
import { FreemiumConnectedBanner } from 'reactApp/ui/FreemiumConnectedBanner/FreemiumConnectedBanner';
import { OverquotaBanner } from 'reactApp/ui/OverquotaBanner/OverquotaBanner';
import { PersonalizationTopBanner } from 'reactApp/ui/PersonalizationTopBanner/PersonalizationTopBanner';
import { PreOverquotaBanner } from 'reactApp/ui/PreOverquotaBanner/PreOverquotaBanner';
import { isPreOverquotaBannerPeriodPassedCheck } from 'reactApp/ui/PreOverquotaBanner/PreOverquotaBanner.helpers';
import { UnbindCardBanner } from 'reactApp/ui/UnbindCardBanner/UnbindCardBanner';
import { VigboEndTariffBanner } from 'reactApp/ui/VigboEndTariffBanner/VigboEndTariffBanner';
import { VigboRenewRemindBanner } from 'reactApp/ui/VigboRenewRemindBanner/VigboRenewRemindBanner';
import { ConditionsCheckType, getCheckAllConditions } from 'reactApp/utils/conditionChecker';

const overquotaCheckAllConditionsTrue = getCheckAllConditions(overquotaDebugLogger, ConditionsCheckType.FirstFalse);
const preOverquotaCheckAllConditionsTrue = getCheckAllConditions(preOverquotaDebugLogger, ConditionsCheckType.FirstFalse);
const freemiumCheckAllConditionsTrue = getCheckAllConditions(freemiumDebugLogger, ConditionsCheckType.FirstFalse);

export const useBanner = () => {
    const [show, setShow] = useState<boolean>(true);
    const isCorp = useSelector(UserSelectors.isCorpUser);
    const isBizUser = useSelector(UserSelectors.isBizUser);
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const isOverquota = useSelector(UserQuotaSelectors.isOverquotaBySpaceAndApi);
    const { used: usedPercent } = useSelector(UserQuotaSelectors.getFilledPercents);
    const storage = useSelector(getCurrentStorage);
    const { isAllDocuments, isFamilyPage, isFamilyAlbums, isFamilyFolders, isHome } = getStorage(storage);
    const isFreemium = useIsFreemium();
    const isPersonalizationTopBanner = usePersonalizationTopBanner();

    const isMobile = useSelector(EnvironmentSelectors.isMobile);
    const isVigboActiveTariff = useSelector(hasActiveVigboTariff);
    const { isLoading, isLoaded } = useSelector(getBillingDataLoadingState);
    const vigboAlbums = useSelector(getVigboAlbums);
    const vigboLinkState = useSelector(getVigboLinkState);
    const showVigboEndTariffBanner =
        (vigboLinkState === VigboAccountLinkState.linked || forceVigboLinkedAccount) &&
        storage === EStorageType.vigbo &&
        !isMobile &&
        !isLoading &&
        isLoaded &&
        !isVigboActiveTariff &&
        vigboAlbums.length > 0;

    const vigboSubs = useSelector((state: RootState) => BillingDataSelectors.getSubscriptionsByProductIds(state, vigboTariffs));
    const latestExpireTariff = useMemo(() => {
        const subsWithoutRenew = vigboSubs.filter((sub) => !sub.autorenewal);
        return getLatestExpiredSubscription(subsWithoutRenew);
    }, [vigboSubs]);
    const diffInMs = latestExpireTariff && latestExpireTariff.expires - Date.now();
    const diffInDays = diffInMs && Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const showVigboTariffEndRemindBanner =
        latestExpireTariff &&
        storage === EStorageType.vigbo &&
        !isMobile &&
        !isLoading &&
        isLoaded &&
        diffInDays &&
        diffInDays <= vigboTariffBannerRemindDays;

    const showOverquotaBanner = overquotaCheckAllConditionsTrue('overquotaBanner', {
        featureEnabled: overquotaBannerConfig.enabled,
        isOverquota,
        isNotBiz: !isBizUser,
        isNotOnPrem: !IS_ONPREMISE,
        isNotCorp: !isCorp,
        isNotDocs: !isAllDocuments,

        notShowVigboEndTariffBanner: !showVigboEndTariffBanner,
    });

    const showPreOverquotaBanner = preOverquotaCheckAllConditionsTrue('preOverquotaBanner', {
        preOverquotaBanner: preOverquotaBannerConfig.enable,
        isNotBiz: !isBizUser,
        isNotOnPrem: !IS_ONPREMISE,
        isNotCorp: !isCorp,
        isNotPaidCheck: { check: preQuotaEnablePaid || !isPaidUser, preQuotaEnablePaid, isPaidUser },
        isNotDocs: !isAllDocuments,
        isNotFamilyPage: !isFamilyPage,
        isNotFamilyAlbums: !isFamilyAlbums,
        isNotFamilyFolders: !isFamilyFolders,
        isNotOverquota: !isOverquota,

        overLimitCheck: { check: usedPercent >= preQuotaSizeLimit, usedPercent, preQuotaOfferLimit: preQuotaSizeLimit },
        isPeriodPassed: isPreOverquotaBannerPeriodPassedCheck,

        notShowVigboEndTariffBanner: !showVigboEndTariffBanner,
        notShowOverquotaBanner: !showOverquotaBanner,
        notShowVigboTariffEndRemindBanner: !showVigboTariffEndRemindBanner,
    });

    const { shouldShowBanner: showAiPhotoGenerationEntryPointBanner, setIsHidden: setAiPhotoGenerationEntryPointBannerHidden } =
        useAiPhotoGenerationEntryPointBannerShow();
    const { shouldShowBanner: showDuplicatesEntryPointBanner, setIsHidden: setDuplicatesEntryPointBannerHidden } =
        useDuplicatesEntryPointBanner();

    const { autoRenewalBanner, unbindCardBanner } = useSubscriptionCommunicationBanners();

    if (!show) {
        return null;
    }

    if (showVigboEndTariffBanner) {
        return <VigboEndTariffBanner />;
    }

    if (showOverquotaBanner) {
        return <OverquotaBanner />;
    }

    if (isHome && autoRenewalBanner.shouldShowBanner && autoRenewalBanner.subscription) {
        return (
            <AutoRenewalBanner
                subscription={autoRenewalBanner.subscription}
                hasCard={autoRenewalBanner.hasCard}
                isCardLoaded={autoRenewalBanner.isCardLoaded}
            />
        );
    }

    if (isHome && unbindCardBanner.shouldShowBanner && unbindCardBanner.subscription) {
        return <UnbindCardBanner subscription={unbindCardBanner.subscription} />;
    }

    if (showVigboTariffEndRemindBanner) {
        return <VigboRenewRemindBanner tariff={latestExpireTariff} />;
    }

    if (showPreOverquotaBanner) {
        return <PreOverquotaBanner setShow={setShow} />;
    }

    const showPersonalizationTopBannerConditions: ConditionsWithData = {
        personalizedTopBannerEnabled,
        isHome,
        isPersonalizationTopBanner,
        autoRenewalBannerDataReady: !autoRenewalBanner.shouldLoadCard || autoRenewalBanner.isDataReady,
        unbindCardBannerDataReady: !unbindCardBanner.shouldLoadCard || unbindCardBanner.isDataReady,
    };

    const { result: showPersonalizationTopBanner } = checkConditions(showPersonalizationTopBannerConditions);

    if (showPersonalizationTopBanner) {
        return <PersonalizationTopBanner />;
    }

    const showFreemiumConnectedBanner = freemiumCheckAllConditionsTrue('freemiumConnectedBanner', {
        freemiumConnectedBanner,
        isSoftOrHardFreemium: isSoftFreemium || isHardFreemium,
        isFreemium,
        isHome,
        isNotOverquota: !isOverquota,
        isNotBiz: !isBizUser,
        isNotCorp: !isCorp,

        notShowVigboEndTariffBanner: !showVigboEndTariffBanner,
        notShowOverquotaBanner: !showOverquotaBanner,
        notShowVigboTariffEndRemindBanner: !showVigboTariffEndRemindBanner,
        notShowPreOverquotaBanner: !showPreOverquotaBanner,
    });

    if (showFreemiumConnectedBanner) {
        return <FreemiumConnectedBanner />;
    }

    if (showDuplicatesEntryPointBanner) {
        return <DuplicatesPromoBanner setHidden={setDuplicatesEntryPointBannerHidden} />;
    }

    if (showAiPhotoGenerationEntryPointBanner) {
        return <AiPhotoGenerationPromoBanner setHidden={setAiPhotoGenerationEntryPointBannerHidden} />;
    }

    return null;
};
